import SEO from "@/components/SEO";
import {
  Blockquote,
  CTAButton,
  H2,
  HeroH1,
  InlineLink,
  P,
  SeoPageShell,
  SpeakerLine,
} from "./seo/SeoPageLayout";

const ReactiveAbuseExamples = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Reactive Abuse Examples: When You Become the Villain in Their Story"
        description="Reactive abuse is when someone provokes you until you finally snap — then points to your reaction as proof you were the problem all along. Real examples of how it works in text."
        path="/reactive-abuse-examples"
      />

      <HeroH1>Reactive Abuse Examples: When You Finally React and Get Blamed for It</HeroH1>

      <P>
        Reactive abuse is the experience of being slowly wound up — through small jabs,
        deniable provocations, a tone you can't quite point to — until you finally raise your
        voice, send the angry text, or say the cutting thing. And the instant you do, your
        reaction becomes the only thing that gets discussed.
      </P>
      <P>
        Suddenly the conversation is no longer about anything that happened before you snapped.
        It is about how you snapped. This is part of{" "}
        <InlineLink to="/why-do-i-feel-exhausted-after-conversations">
          why these conversations leave you so exhausted →
        </InlineLink>{" "}
        even when, on paper, nothing major occurred.
      </P>

      <H2>What reactive abuse actually looks like</H2>
      <P>The provocation is quiet. The reaction is loud. That asymmetry is the whole tactic.</P>

      <Blockquote>
        <SpeakerLine speaker="Them" line="Wow. You're really wearing that?" />
        <SpeakerLine speaker="You" line="What's wrong with what I'm wearing?" />
        <SpeakerLine speaker="Them" line="Nothing. Forget I said anything. You're so defensive lately." />
        <SpeakerLine speaker="You" line="I'm not being defensive. You're the one who said something." />
        <SpeakerLine speaker="Them" line="Okay, see, THIS is what I'm talking about. You can't even take a comment." />
      </Blockquote>

      <Blockquote>
        <SpeakerLine speaker="Them" line="I noticed you didn't text me back for two hours." />
        <SpeakerLine speaker="You" line="I was in a meeting." />
        <SpeakerLine speaker="Them" line="It's fine. I'm used to being a low priority." />
        <SpeakerLine speaker="You" line="Please don't do this every time. I was working." />
        <SpeakerLine speaker="Them" line="So now I'm 'doing' something. Great. Add that to the list." />
        <SpeakerLine speaker="You" line="Forget it. I can't deal with this right now." />
        <SpeakerLine speaker="Them" line="You ALWAYS shut down. That's the real problem here." />
      </Blockquote>

      <Blockquote>
        <SpeakerLine speaker="Them" line="If you actually cared, you'd have remembered." />
        <SpeakerLine speaker="You" line="I literally remembered yesterday. You're being unfair." />
        <SpeakerLine speaker="Them" line="Listen to your tone. Are we recording this? Because I'd love anyone to hear how you speak to me." />
      </Blockquote>

      <H2>Why your reaction gets all the attention</H2>
      <P>
        The provocation is small and deniable. It can be reframed as a joke, a comment, a
        concern, a question. The reaction is large and visible — a raised voice, a sharp text, a
        slammed door.
      </P>
      <P>
        Observers anchor on what they can see. So do you, when you look back at it. Your
        reaction is the loudest moment in the exchange, so it becomes the moment the exchange
        was "about." The slow setup that made the reaction inevitable disappears from the
        record — which is why{" "}
        <InlineLink to="/ai-detect-narcissistic-abuse">
          forensic analysis that reads the whole conversation, not one message
        </InlineLink>
        , changes what you can actually see.
      </P>

      <CTAButton />

      <H2>How to tell reactive abuse from a normal argument</H2>
      <P>
        A normal conflict does not require you to have been steadily wound up beforehand. You
        don't have to be primed for fifteen minutes to argue with a friend about where to eat
        dinner.
      </P>
      <P>Observable distinctions, written down so you can check them honestly:</P>
      <P>
        The pattern repeats. You keep finding yourself reacting to things that keep happening.
        You keep apologizing for reactions, but the conditions that produced the reactions never
        change. You leave each exchange feeling like the problem, even when, if you write down
        what was actually said, you weren't.
      </P>

      <H2>Naming it changes what you can do about it</H2>
      <P>
        Once you can name the sequence — provocation, reaction, weaponized reaction — you stop
        living inside it. You start watching for the first move instead of being startled by the
        third.
      </P>
      <P>
        Reactive abuse very often pairs with a blame-flip pattern called DARVO, where the
        provoker reframes themselves as the victim of your reaction.{" "}
        <InlineLink to="/darvo-examples">
          See DARVO examples →
        </InlineLink>{" "}
        Reading this kind of dynamic forensically is{" "}
        <InlineLink to="/articulation-before-adjudication">
          the emerging category these tools belong to →
        </InlineLink>
      </P>

      <CTAButton />
    </SeoPageShell>
  );
};

export default ReactiveAbuseExamples;
